import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

import underlineimage from "../assets/images/title-shape.png";
import gamebg from "../assets/images/Esportsec/esportsgamesec.png";

import bgmi from "../assets/images/games/BGMI.png";
import ffm from "../assets/images/games/FFM.png";
import codm from "../assets/images/games/CODM.png";
import asphalt9 from "../assets/images/games/Asphalt_9.png";
import bombsquad from "../assets/images/games/BombSquad.png";
import brawlstars from "../assets/images/games/BRAWL_STARS.png";
import bulletLeague from "../assets/images/games/Bullet_League.png";
import clashRoyale from "../assets/images/games/Clash_Royale.png";
import csgo from "../assets/images/games/CS_GO.png";
import fifa21 from "../assets/images/games/FIFA_21_PC.png";
import fifaMobile from "../assets/images/games/Fifa_mobile.png";
import marioKart from "../assets/images/games/Mario_kart_Tour.png";
import pokemonUnite from "../assets/images/games/Pokemon_Unite.png";
import pubgNewState from "../assets/images/games/Pubg_New_State.png";
import stumbleGuys from "../assets/images/games/Stumble_Guys.png";
import turboLeague from "../assets/images/games/Turbo_league.png";
import valorant from "../assets/images/games/Valorant.png";
import wcc3 from "../assets/images/games/Wcc3.png";

import { useTranslation } from "react-i18next";

interface GameItem {
  title: string;
  image: string;
}

const gameData: GameItem[] = [
  { title: "BGMI", image: bgmi },
  { title: "FREE FIRE MAX", image: ffm },
  { title: "COD MOBILE", image: codm },
  { title: "ASPHALT 9", image: asphalt9 },
  { title: "BOMB SQUAD", image: bombsquad },
  { title: "BRAWL STARS", image: brawlstars },
  { title: "BULLET LEAGUE", image: bulletLeague },
  { title: "CLASH ROYALE", image: clashRoyale },
  { title: "CS:GO", image: csgo },
  { title: "FIFA 21 PC", image: fifa21 },
  { title: "FIFA MOBILE", image: fifaMobile },
  { title: "MARIO KART TOUR", image: marioKart },
  { title: "POKEMON UNITE", image: pokemonUnite },
  { title: "PUBG NEW STATE", image: pubgNewState },
  { title: "STUMBLE GUYS", image: stumbleGuys },
  { title: "TURBO LEAGUE", image: turboLeague },
  { title: "VALORANT", image: valorant },
  { title: "WCC3", image: wcc3 },
];

const GamesSec: React.FC = () => {
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const { t } = useTranslation();

  const handleSlideChange = () => {
    if (!swiperRef.current) return;
    setIsBeginning(swiperRef.current.isBeginning);
    setIsEnd(swiperRef.current.isEnd);
  };

  useEffect(() => {
    const swiper = swiperRef.current;
    const prevEl = prevRef.current;
    const nextEl = nextRef.current;

    if (
      swiper &&
      prevEl &&
      nextEl &&
      swiper.params.navigation &&
      typeof swiper.params.navigation !== "boolean"
    ) {
      swiper.params.navigation.prevEl = prevEl;
      swiper.params.navigation.nextEl = nextEl;
      swiper.navigation.destroy();
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, []);

  return (
    <div
      className="py-16 px-4 md:px-10 lg:px-20 bg-cover bg-no-repeat bg-center"
      style={{ backgroundImage: `url(${gamebg})` }}
    >
      <div className="text-center mx-auto mb-12">
        <h2 className="text-[36px] md:text-[48px] font-oswald font-extrabold text-[#ffffff]">
          {t("games.heading")}
        </h2>
        <img
          src={underlineimage}
          alt="underline"
          className="mx-auto mt-2 mb-6 w-[120px] h-auto"
        />
        <p className="text-[16px] md:text-[18px] font-proxima font-medium text-[#ffffff] leading-relaxed">
          Over 1000 daily tournaments for your favorite games. Play & win exciting rewards!
        </p>
      </div>

      <div className="relative max-w-[78rem] mx-auto">
        <Swiper
          onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
          onSlideChange={handleSlideChange}
          slidesPerView={1}
          spaceBetween={20}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper: SwiperType) => {
            if (
              swiper.params.navigation &&
              typeof swiper.params.navigation !== "boolean"
            ) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          modules={[Navigation]}
          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 6 }, // 👈 Show 6 cards for large screens
          }}
          className="pb-12"
        >
          {gameData.map((game, index) => (
            <SwiperSlide key={index}>
              <div className="w-[188px] h-[254px] rounded-2xl overflow-hidden shadow-xl mx-auto transition-transform duration-300 transform hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,0,0.4)]">
                <img
                  src={game.image}
                  alt={game.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Buttons */}
        <div className="flex justify-center items-center gap-11 mt-11">
          <button
            ref={prevRef}
            disabled={isBeginning}
            className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md transition ${
              isBeginning
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-yellow-500 hover:bg-yellow-600 text-black"
            }`}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>

          <button
            ref={nextRef}
            disabled={isEnd}
            className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md transition ${
              isEnd
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-yellow-500 hover:bg-yellow-600 text-black"
            }`}
          >
            <i className="fa-solid fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GamesSec;
